/**
 * 設定 → ユーザー管理 → 修正のシート（040、docs/29 §5 → #156、docs/65 §5・Figma「設定の残り（10/9）」㉑。前は別の画面）。管理者だけ
 *   ・一覧の画面（SettingsUsers.tsx）が /settings/users/:id で出す。スマホ版＝下から、PC版＝真ん中の枠
 *   ・頭「取消｜ユーザーを修正｜保存」（保存は直したときだけ押せる。id saveUser）。頭に人のアイコン 64・氏名・メール
 *   ・権限のセグメント（id roleSeg）／「利用」スイッチ（#186 で「使える」から「この会社で利用できる」→ #287 で「利用」＝オフが停止中）。メールは見るだけ（変更は #63）
 *   ・氏名（#152、docs/61 ★b）：参加した人の姓・名は見るだけ（その人のほかの会社の画面まで変わるため。本人が個人設定で変える。API も 400 USER_NAME_OWNED）。
 *     注「氏名は本人が個人設定で変えます」。設定待ちの利用者は今までどおり直せる（利用を開始すると、その人に氏名があれば人の氏名になる＝★c）
 *   ・一番下に「この会社から除外」（朱、id removeBtn）と注。押すと確かめ（名前は残る）。
 *     設定待ちの利用者は代わりに「案内メール再送」（藍）と「追加取消」（朱）（#287 で「招待メール再送」「招待取消」から）
 *   ・自分は 停止中にできない・除外できない。最後の管理者は 担当者にできない・停止中にできない・除外できない（API 409、画面でも押せない形＋注1行）
 */
import { ACCOUNT_NAME_MSG, REST_MSG, ROLE_LABEL, USER_MSG, type UserRole, type UsersPage as UsersPageData, type UserRow } from "@genbaryoku/contract";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { api, ApiError, messageOf } from "../api.js";
import { differs, LeaveConfirm } from "../components/BackButton.jsx";
import { GroupDangerRow, GroupInputRow, GroupList, GroupSection, GroupSwitchRow, GroupValueRow } from "../components/GroupList.jsx";
import { PersonIcon } from "../components/PersonIcon.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { Sheet } from "../components/Sheet.jsx";
import { ErrorText, Help, guardComposingEnter, useFieldIds } from "../components/ui.jsx";
import { t } from "../labels.js";

type Saved = UsersPageData & { message: string };
type Form = { familyName: string; givenName: string; role: UserRole; enabled: boolean };

export function UserEditSheet({ open, target, users, onClose, onSaved }: { open: boolean; target: UserRow; users: UserRow[]; onClose: () => void; onSaved: (p: Saved) => void }) {
  const ids = useFieldIds(["family", "given"] as const);
  const roleId = useId();
  const baseline: Form = { familyName: target.familyName, givenName: target.givenName, role: target.role, enabled: target.status !== "disabled" };
  const [form, setForm] = useState<Form>(baseline);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | undefined>();
  const [dlg, setDlg] = useState(false);
  const [askLeave, setAskLeave] = useState(false);
  const dirty = differs(form, baseline);

  const fail = (e: unknown) => {
    if (e instanceof ApiError && e.problem.errors) {
      setErrors(Object.fromEntries(Object.entries(e.problem.errors).map(([k, v]) => [k, String(v)])));
      return;
    }
    setServerError(messageOf(e));
  };
  const save = useMutation({
    mutationFn: (v: { familyName?: string; givenName?: string; role: UserRole; enabled: boolean }) => api<Saved>("PUT", `/users/${target.id}`, v),
    onSuccess: onSaved,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => api<Saved>("POST", `/users/${target.id}/remove`),
    onSuccess: (p) => {
      setDlg(false);
      onSaved(p);
    },
    onError: (e) => {
      setDlg(false);
      setServerError(messageOf(e));
    },
  });
  const resend = useMutation({ mutationFn: () => api<Saved>("POST", `/users/${target.id}/resend`), onSuccess: onSaved, onError: (e) => setServerError(messageOf(e)) });
  const cancelInvite = useMutation({ mutationFn: () => api<Saved>("DELETE", `/users/${target.id}/invite`), onSuccess: onSaved, onError: (e) => setServerError(messageOf(e)) });
  const pending = save.isPending || remove.isPending || resend.isPending || cancelInvite.isPending;

  const you = target.isSelf;
  const activeAdmins = users.filter((u) => u.role === "admin" && u.status === "active").length;
  const last = target.role === "admin" && target.status === "active" && activeAdmins === 1;
  const name = target.name || target.email;
  const invited = target.status === "invited";

  const submit = () => {
    if (!dirty || pending) return;
    setErrors({});
    setServerError(undefined);
    // 利用を開始した人の氏名は送らない（★b）。設定待ちの利用者だけ姓・名を直せる
    if (!invited) {
      save.mutate({ role: form.role, enabled: form.enabled });
      return;
    }
    if (!form.familyName.trim()) {
      setErrors({ familyName: USER_MSG.familyNameRequired });
      document.getElementById(ids.family)?.focus();
      return;
    }
    save.mutate({ familyName: form.familyName.trim(), givenName: form.givenName.trim(), role: form.role, enabled: form.enabled });
  };
  const cancel = () => {
    if (dirty && !pending) setAskLeave(true);
    else onClose();
  };
  /** 権限・利用の節の注：自分・最後の管理者の制限、利用を開始した人の氏名の注 */
  const notes = [
    last ? <span key="la" className="shu" id="lastAdminNote">{REST_MSG.lastAdminRole.replace("{name}", name)}</span> : null,
    you ? <span key="se" id="selfNote">{USER_MSG.selfDisable}</span> : last ? <span key="la2" id="lastAdminNote2">{REST_MSG.lastAdminEnabled}</span> : null,
    !invited ? <span key="no" id="nameOwnedNote">{ACCOUNT_NAME_MSG.owned}</span> : null,
  ].filter(Boolean);
  const inv = (k: string, id: string) => (errors[k] ? { "aria-invalid": true as const, "aria-describedby": `${id}-err` } : {});

  return (
    <>
      <Sheet
        open={open}
        title={REST_MSG.editTitle}
        cancelLabel="取消"
        doneLabel={save.isPending ? "保存中…" : "保存"}
        doneDisabled={!dirty || pending}
        onCancel={cancel}
        onDone={submit}
        className="usersheet"
        doneId="saveUser"
        testId="user-sheet"
        formTestId="editForm"
        cancelTestId="user-sheet-cancel"
      >
        <div className="phead usersheet-head" data-testid="user-name-ro">
          <PersonIcon familyName={target.familyName} givenName={target.givenName} size={64} />
          <span className="pname" id="fNameRO">
            {target.name || REST_MSG.noName}
            {you ? <span className="you">{REST_MSG.you}</span> : null}
          </span>
          <span className="pmail" id="fEmailRO">
            {target.email}
          </span>
        </div>
        <ErrorText>{serverError}</ErrorText>
        {invited ? (
          <GroupSection title={REST_MSG.sectionName} data-testid="user-sec-name">
            <GroupList plain>
              <GroupInputRow id={ids.family} label="姓" wide error={errors.familyName}>
                <input id={ids.family} className="giinp" maxLength={50} value={form.familyName} {...inv("familyName", ids.family)} onKeyDown={guardComposingEnter} onChange={(e) => setForm({ ...form, familyName: e.target.value })} />
              </GroupInputRow>
              <GroupInputRow id={ids.given} label="名" wide error={errors.givenName}>
                <input id={ids.given} className="giinp" maxLength={50} value={form.givenName} {...inv("givenName", ids.given)} onKeyDown={guardComposingEnter} onChange={(e) => setForm({ ...form, givenName: e.target.value })} />
              </GroupInputRow>
            </GroupList>
          </GroupSection>
        ) : null}
        <GroupSection
          note={notes.length ? <span className="gsecnote-lines">{notes}</span> : undefined}
          data-testid="user-sec-role"
        >
          <GroupList plain>
            <GroupValueRow
              title={REST_MSG.role}
              titleId={roleId}
              className="grow-seg is-in"
              value={
                <Segmented
                  id="roleSeg"
                  options={(["member", "admin"] as UserRole[]).map((r) => ({ value: r, label: ROLE_LABEL[r], testId: `role-${r}` }))}
                  value={form.role}
                  onChange={(r) => {
                    if (r === "member" && last) return;
                    setForm({ ...form, role: r });
                  }}
                  labelledBy={roleId}
                  disabled={last}
                />
              }
            />
            <GroupSwitchRow
              label={
                <>
                  {REST_MSG.enabled}
                  <Help testId="help-user-enabled" text={REST_MSG.enabledHelp} />
                </>
              } name={REST_MSG.enabled} checked={form.enabled} disabled={you || last} onChange={(v) => setForm({ ...form, enabled: v })} testId="sw-user-enabled" rowTestId="user-enabled-row" />
          </GroupList>
        </GroupSection>

        {invited ? (
          <GroupSection data-testid="user-sec-invite">
            <GroupList plain>
              <GroupDangerRow tone="ai" title={REST_MSG.resend} buttonId="resendInvite" disabled={pending} onClick={() => resend.mutate()} />
              <GroupDangerRow title={REST_MSG.cancelInvite} buttonId="cancelInvite" disabled={pending} onClick={() => cancelInvite.mutate()} />
            </GroupList>
          </GroupSection>
        ) : you ? (
          <p className="banksheet-note" id="selfRemoveNote">
            {USER_MSG.selfRemove}
          </p>
        ) : (
          <GroupSection note={last ? <span className="shu">{REST_MSG.lastAdminRemove}</span> : t(REST_MSG.removeNote)} data-testid="user-sec-remove">
            <GroupList plain>
              <GroupDangerRow title={REST_MSG.remove} buttonId="removeBtn" disabled={last || pending} onClick={() => setDlg(true)} />
            </GroupList>
          </GroupSection>
        )}
      </Sheet>
      {dlg ? (
        <div className="dim" id="dim" onClick={(e) => e.target === e.currentTarget && setDlg(false)}>
          <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="dlgT" onKeyDown={(e) => e.key === "Escape" && setDlg(false)}>
            <div className="dh" id="dlgT">
              {REST_MSG.removeAsk.replace("{name}", name)}
            </div>
            <div className="db">
              {/* #92 決め1：除外の前の段落と確かめの文が重なっていたので、確かめの中の1つにまとめた。#287 で「席が1つ空きます」をやめた */}
              <p style={{ margin: 0 }}>{t(REST_MSG.removeBody)}</p>
            </div>
            <div className="df">
              <button type="button" className="btn quiet" id="dlgNo" autoFocus onClick={() => setDlg(false)}>
                取消
              </button>
              <button type="button" className="btn shu" id="dlgYes" disabled={remove.isPending} onClick={() => remove.mutate()}>
                {REST_MSG.removeYes}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {askLeave ? (
        <LeaveConfirm
          onStay={() => setAskLeave(false)}
          onLeave={() => {
            setAskLeave(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
